import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Plus, Save, Trash2, Loader2, Pencil, X, Check } from "lucide-react";
import Nav from "../components/Nav";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import BulkImport from "../components/BulkImport";
import CardAudio from "../components/CardAudio";

function CardRow({ card, courseId, onChanged, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ front: card.front, back: card.back, example: card.example || "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft({ front: card.front, back: card.back, example: card.example || "" });
  }, [card]);

  const save = async () => {
    if (!draft.front.trim() || !draft.back.trim()) return toast.error("Front & back required");
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/cards/${card.id}`, draft);
      toast.success("Card saved");
      setEditing(false);
      onChanged?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete card "${card.front} → ${card.back}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/cards/${card.id}`);
      toast.success("Card deleted");
      onDeleted?.(card.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="brutal-card-sm p-4 bg-brand-cream" data-testid={`admin-card-row-${card.id}`}>
        <div className="grid md:grid-cols-2 gap-3">
          <input value={draft.front} onChange={(e) => setDraft({ ...draft, front: e.target.value })} data-testid={`admin-card-front-${card.id}`} placeholder="Front (target language)" className="brutal-input" />
          <input value={draft.back} onChange={(e) => setDraft({ ...draft, back: e.target.value })} data-testid={`admin-card-back-${card.id}`} placeholder="Back (English)" className="brutal-input" />
        </div>
        <input value={draft.example} onChange={(e) => setDraft({ ...draft, example: e.target.value })} data-testid={`admin-card-example-${card.id}`} placeholder="Example sentence (optional)" className="brutal-input mt-3" />
        <div className="mt-3">
          <div className="text-xs font-bold uppercase text-brand-navy/60 mb-2">Pronunciation audio (optional)</div>
          <CardAudio
            cardId={card.id}
            existingUrl={card.audio_url ? `${process.env.REACT_APP_BACKEND_URL}${card.audio_url}` : null}
            onUploaded={(url) => onChanged?.({ ...card, audio_url: url })}
          />
        </div>
        <div className="mt-3 flex gap-2 justify-end">
          <button onClick={() => { setEditing(false); setDraft({ front: card.front, back: card.back, example: card.example || "" }); }} className="brutal-btn-white text-xs py-2" disabled={busy}>
            <X className="w-3.5 h-3.5" strokeWidth={3} /> Cancel
          </button>
          <button onClick={save} data-testid={`admin-card-save-${card.id}`} className="brutal-btn-pink text-xs py-2" disabled={busy}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={3} />} Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="brutal-card-sm p-4 bg-white flex items-start gap-4 flex-wrap" data-testid={`admin-card-row-${card.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold uppercase text-brand-navy/50">Front</div>
        <div className="font-display font-bold text-brand-navy truncate">{card.front}</div>
        <div className="text-xs font-bold uppercase text-brand-navy/50 mt-1">Back</div>
        <div className="font-display font-bold text-brand-pink truncate">{card.back}</div>
        {card.example && <div className="text-brand-navy/60 italic text-sm mt-1 truncate">"{card.example}"</div>}
        {card.audio_url && (
          <audio
            controls
            src={`${process.env.REACT_APP_BACKEND_URL}${card.audio_url}`}
            className="h-8 mt-2 max-w-full"
            data-testid={`card-audio-inline-${card.id}`}
          />
        )}
      </div>
      <div className="flex gap-2">
        <button onClick={() => setEditing(true)} data-testid={`admin-card-edit-${card.id}`} className="brutal-btn-yellow text-xs py-1.5">
          <Pencil className="w-3.5 h-3.5" strokeWidth={3} /> Edit
        </button>
        <button onClick={remove} data-testid={`admin-card-delete-${card.id}`} className="brutal-btn-pink text-xs py-1.5" disabled={busy}>
          <Trash2 className="w-3.5 h-3.5" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

export default function AdminCards() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [cards, setCards] = useState([]);
  const [draft, setDraft] = useState({ front: "", back: "", example: "" });
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const [c, cs] = await Promise.all([
      api.get(`/courses/${id}`),
      api.get(`/courses/${id}/cards`),
    ]);
    setCourse(c.data);
    setCards(cs.data);
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const addCard = async (e) => {
    e.preventDefault();
    if (!draft.front.trim() || !draft.back.trim()) return toast.error("Front & back required");
    setAdding(true);
    try {
      const { data } = await api.post(`/admin/courses/${id}/cards`, draft);
      setCards((cs) => [...cs, data]);
      setDraft({ front: "", back: "", example: "" });
      toast.success("Card added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg" data-testid="admin-cards-page">
      <Nav />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <button onClick={() => nav("/admin")} className="brutal-btn-white text-sm py-2 mb-4" data-testid="admin-cards-back">
          <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back to Studio
        </button>

        {course && (
          <div className="brutal-card p-6 bg-white mb-6 flex items-center gap-4 flex-wrap">
            <div className="w-16 h-16 rounded-xl border-2 border-brand-ink flex items-center justify-center text-3xl shrink-0" style={{ backgroundColor: course.color || "#FFD166" }}>
              {course.flag}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-3xl font-bold text-brand-navy truncate">{course.title}</h1>
              <p className="text-brand-navy/70 text-sm">
                {course.language} · {cards.length} cards · {course.is_official ? "Official" : "Community"}
              </p>
            </div>
            <Link to={`/courses/${id}`} className="brutal-btn-white text-sm py-2">Preview</Link>
          </div>
        )}

        {/* Bulk import */}
        <BulkImport
          courseId={id}
          language={course?.language}
          onImported={(newCards) => setCards((cs) => [...cs, ...newCards])}
        />

        {/* Add card */}
        <div className="brutal-card p-6 bg-brand-yellow mb-6">
          <h2 className="font-display text-xl font-bold text-brand-navy mb-3 flex items-center gap-2">
            <Plus className="w-5 h-5" strokeWidth={3} /> Add a card
          </h2>
          <form onSubmit={addCard} className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <input value={draft.front} onChange={(e) => setDraft({ ...draft, front: e.target.value })} data-testid="admin-new-card-front" placeholder={`Front (${course?.language || "target"})`} className="brutal-input bg-white" />
              <input value={draft.back} onChange={(e) => setDraft({ ...draft, back: e.target.value })} data-testid="admin-new-card-back" placeholder="Back (English)" className="brutal-input bg-white" />
            </div>
            <input value={draft.example} onChange={(e) => setDraft({ ...draft, example: e.target.value })} data-testid="admin-new-card-example" placeholder="Example sentence (optional)" className="brutal-input bg-white" />
            <div className="flex justify-end">
              <button type="submit" disabled={adding} data-testid="admin-new-card-add" className="brutal-btn-pink text-sm py-2">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={3} />} Add card
              </button>
            </div>
          </form>
        </div>

        {/* Card list */}
        <div className="space-y-3">
          <AnimatePresence>
            {cards.map((c) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CardRow
                  card={c}
                  courseId={id}
                  onChanged={(updated) => setCards((cs) => cs.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
                  onDeleted={(cardId) => setCards((cs) => cs.filter((x) => x.id !== cardId))}
                />
              </motion.div>
            ))}
          </AnimatePresence>
          {cards.length === 0 && (
            <div className="brutal-card p-10 text-center bg-white">
              <p className="text-brand-navy/70">This deck has no cards yet. Add one above ↑</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
